<template>
  <div id="dashboard">
    <Hero hero-image="./img/crew-22256-unsplash.png"/>
    <div class="filters">
      <Dropdown
        v-bind:filterTitle="filterTitle"
        v-bind:filterList="filterList"
        v-on:filter-change="updateFilter" />
    </div>
    <Card v-for="card in cards" :key="card.id"
      v-bind="card"  />
    <Modal
      v-bind:modal-title="modalTitle"
      v-bind:modal-body="modalBody"
      v-bind:is-active="modalIsActive"
      v-on:close-modal="closeModal" />
  </div>
</template>

<script>
// unknown = '#CCCCCC';             // 0 Gray
// unstarted = '#F34329';           // 1 Red
// readyForDev = '#F37829';         // 2 Orange
// inDev = '#F5A623';               // 3 Warm Yellow
// readyForReview = '#F5C423';      // 4 yellow
// readyForDeploy = '#7CB141';      // 5 Green
// completed = '#0787BA';           // 6 Blue

import Card from '../components/Card.vue';
import Dropdown from '../components/Dropdown.vue';
import Hero from '../components/Hero.vue';
import API from '../services/clubhouse';
import Modal from '../components/Modal.vue';

export default {
  name: 'Dashboard',
  components: {
    Card,
    Dropdown,
    Hero,
    Modal,
  },
  data() {
    return {
      sharedStore: this.$root.store,
      cards: [],
      loading: true,
      modalTitle: '',
      modalBody: '',
      modalIsActive: false,
      labelFilter: '',
      filterTitle: 'Filter by...',
      filterList: [],
      omitLabels: ['isHidden', 'Internal'],
    };
  },
  created() {
    const token = this.sharedStore.getToken();

    // If we do NOT have a token from...somewhere...
    if (token === null || !token.length > 0) {
      window.location.href = '/';
    }
  },
  mounted() {
    this.getEpics();
    this.$root.$on('epics-set', () => {
      this.getMembers();
    });
    this.$root.$on('members-set', () => {
      this.parseEpicSummary();
    });
  },
  methods: {
    getEpics() {
      API.getEpics()
        .then((data) => {
          if (data.error) {
            this.modalTitle = 'Server Error';
            this.modalBody = 'There was a problem retrieving data. Please try agian or contact the administrator.';
            this.modalIsActive = true;
          } else {
            const filteredData = this.filterData(data);
            this.sharedStore.setEpics(filteredData);
            this.$root.$emit('epics-set');
          }
        })
        .catch(error => console.error(error))
        .finally(() => {
          // Done
        });
    },
    getMembers() {
      API.getMembers()
        .then((data) => {
          if (data.error) {
            this.modalTitle = 'Server Error';
            this.modalBody = 'There was a problem retrieving data. Please try agian or contact the administrator.';
            this.modalIsActive = true;
          } else {
            this.sharedStore.setMembers(data);
            this.$root.$emit('members-set');
          }
        })
        .catch(error => console.error(error))
        .finally(() => {
          this.loading = false;
        });
    },
    filterData(data) {
      // TODO: Consider moving into a shared `utils` library
      const filteredData = [];
      if (this.sharedStore.state.filter !== '') {
        // eslint-disable-next-line
        for (let i=0; i<data.length; i++ ) {
          if (data[i].labels.length > 0) {
            // eslint-disable-next-line
            for (let x=0; x<data[i].labels.length; x++ ) {
              if (data[i].labels[x].name.toLowerCase() === this.sharedStore.state.filter) {
                filteredData.push(data[i]);
              }
            }
          }
        }

        return filteredData;
      }
      return data;
    },
    epicProgress(epic) {
      const totalStories = epic.stats.num_stories_started +
        epic.stats.num_stories_unstarted +
        epic.stats.num_stories_done;

      let inProgress = 0;
      let areCompleted = 0;
      let areUnstarted = 0;

      if (totalStories !== 0) {
        inProgress = Math.round((epic.stats.num_stories_started / totalStories) * 100);
        areCompleted = Math.round((epic.stats.num_stories_done / totalStories) * 100);
        areUnstarted = Math.round((epic.stats.num_stories_unstarted / totalStories) * 100);
      }

      return { progress: inProgress, completed: areCompleted, unstarted: areUnstarted };
    },
    hasStories(epic) {
      let hasStories = false;
      const storyStats = epic.stats.num_stories_unestimated +
        epic.stats.num_stories_started +
        epic.stats.num_stories_unstarted +
        epic.stats.num_stories_done;

      if (storyStats > 0) {
        hasStories = true;
      }

      return hasStories;
    },
    isHidden(epic) {
      let isHidden = false;
      epic.labels.forEach((label) => {
        if (this.omitLabels.includes(label.name)) {
          isHidden = true;
        }
      });

      return isHidden;
    },
    parseEpicSummary() {
      const epicData = [];

      this.sharedStore.state.epics.forEach((element) => {
        const hasStories = this.hasStories(element);
        const isHidden = this.isHidden(element);

        if (!isHidden && hasStories) {
          const epicProgress = this.epicProgress(element);

          let statusValue = 0;
          let statusColor = 'unknown';
          let statusText = 'Unknown';

          switch (element.state) {
            case 'to do':
              statusValue = 1;
              statusColor = 'readyForDev';
              statusText = 'Unstarted';
              break;
            case 'in progress':
              statusValue = 2;
              statusColor = 'readyForDeploy';
              statusText = 'In Development';
              break;
            case 'done':
              statusValue = 3;
              statusColor = 'completed';
              statusText = 'Completed';
              break;
            default:
              // This should never happen
              break;
          }

          const owners = [];

          if (element.owner_ids.length > 0) {
            element.owner_ids.forEach((owner) => {
              this.sharedStore.state.members.forEach((member) => {
                if (member.id === owner) {
                  owners.push(member);
                }
              });
            });
          } else {
            // No owners currently assigned
            owners.push({ profile: { name: 'NA' } });
          }

          // Generate an "owners" string
          let cardOwners = '';
          let cardOwnerAvatar = '';
          let isFirst = true;

          owners.forEach((owner) => {
            if (isFirst) {
              cardOwners = `${cardOwners}${owner.profile.name}`;
              if (owner.profile !== undefined && owner.profile.display_icon !== undefined) {
                cardOwnerAvatar = owner.profile.display_icon.url;
              } else {
                cardOwnerAvatar = './img/icon-default-avatar.svg';
              }
              isFirst = false;
            } else {
              cardOwners = `${cardOwners}, ${owner.profile.name}`;
            }
          });

          epicData.push({
            id: element.id,
            statusId: statusValue,
            statusColor: `${statusColor}`,
            statusText: `${statusText}`,
            statusProgress: epicProgress.progress,
            statusCompleted: epicProgress.completed,
            statusUnstarted: epicProgress.unstarted,
            cardTitle: element.name,
            cardDescription: element.description,
            cardOwner: cardOwners,
            cardOwnerAvatar: `${cardOwnerAvatar}`,
            cardDate: element.stats.last_story_update,
          });
        }
      });

      this.sharedStore.setSummaryData(epicData);
      this.cards = this.sharedStore.state.summaryData;

      this.generateFilters();
    },
    generateFilters() {
      const allLabels = [];
      const uniqueLabels = [{ id: '', label: 'All' }];
      this.sharedStore.state.epics.forEach((element) => {
        element.labels.forEach((label) => {
          if (!allLabels.includes(label.name)) {
            if (!this.omitLabels.includes(label.name)) {
              allLabels.push(label.name);
              uniqueLabels.push({ id: label.name.toLowerCase(), label: label.name });
            }
          }
        });
      });

      this.filterList = uniqueLabels;
    },
    updateFilter() {
      if (this.sharedStore.state.filter === '') {
        this.filterTitle = 'Filter by All';
      } else {
        this.filterTitle = `Filter by ${this.firstToUpper(this.sharedStore.state.filter)}`;
      }
      this.getEpics();
    },
    closeModal() {
      this.modalIsActive = false;
    },
    firstToUpper(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
  },
};
</script>

<style scoped lang="scss">
  .filters {
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 10px;
    max-width: 768px;
  }
</style>
